import type { Map as ImmutableMap } from 'immutable';
import { DateTime } from 'luxon';
// temporary sandbox for internal purpose only
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { dateSelectors } from 'app-components/flux/date';
import { preferencesV2Actions, preferencesV2Selectors, preferencesV2Types } from 'app-components/flux/preferences-v2';
import { transactionsActions, transactionsSelectors } from 'app-components/flux/transactions';

import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TransactionsHistoryChart from 'components/Charts/TransactionsHistoryChart';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';

const maxMonths = +12;

export const TransactionsSettings = () => {
  const dispatch = useDispatch();

  const dateOnAfterPref: string = useSelector((state) =>
    preferencesV2Selectors.getPreferenceValue(state, 'dateOnAfter'),
  );
  const [dateOnAfterTmp, setDateOnAfterTmp] = React.useState<string>(
    DateTime.fromISO(dateOnAfterPref).minus({ years: 1 }).endOf('year').toISODate() || '1999-12-31',
  );

  const isLoading: boolean = useSelector(transactionsSelectors.getIsLoading);
  const transactions: ImmutableMap<string, any> = useSelector(transactionsSelectors.getResourcesById);
  const dateOnAfterDB: string = useSelector(transactionsSelectors.getDateOnAfter) ?? '';
  const earliestDateOn: string = useSelector(transactionsSelectors.getEarliestDateOn) || '';
  const currentDate = useSelector(dateSelectors.getCurrentDate);
  const monthsTotal = React.useMemo(() => {
    const result = Math.trunc(
      currentDate.plus({ months: maxMonths }).diff(DateTime.fromISO(earliestDateOn), 'months').months,
    );
    return Number.isNaN(result) ? 0 : result;
  }, [currentDate, earliestDateOn]);

  const chartData = React.useMemo(() => {
    const result = Object.fromEntries(Array.from(Array(monthsTotal).keys(), (index) => [maxMonths - index, 0]));
    transactions.forEach((transaction) => {
      const key = Math.trunc(DateTime.fromISO(transaction.postedOn).diff(currentDate, 'months').months);
      result[key] = (result[key] || 0) + 1;
    });
    return Array.from(Object.entries(result), ([key, value]) => ({ months: Number(key), count: value })).sort(
      (data1, data2) => data1.months - data2.months,
    );
  }, [transactions, currentDate, monthsTotal]);

  return (
    <>
      <SettingsHeader
        title='Transactions Settings'
        description='Manage the settings for your transaction history in Simplifi'
      />
      <Paper sx={{ padding: 3 }}>
        <Stack gap={3}>
          <Stack
            sx={(theme) => ({
              backgroundColor: theme.palette.info.light,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.shape.borderRadius,
              padding: theme.spacing(2),
            })}
            gap={1}
          >
            <Typography variant='bodyMedium'>History summary</Typography>
            <Stack direction='row' gap={3} flexWrap='wrap'>
              {[
                { label: 'Local Transactions Count', value: transactions.size },
                { label: 'Earliest Date from Server', value: earliestDateOn },
                { label: 'Date Filter (Preferences)', value: dateOnAfterPref },
                { label: 'Date Filter (Database)', value: dateOnAfterDB || '-' },
              ].map(({ label, value }) => (
                <Stack key={label}>
                  <Typography variant='captionRegular' color='textSecondary'>
                    {label}
                  </Typography>
                  <Typography variant='paragraphMedium'>{value}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
          <Box padding={3}>
            <TransactionsHistoryChart data={chartData} height={300} />
          </Box>
          <Divider />
          <Stack gap={3} justifyContent='flex-start' alignItems='flex-start'>
            <Typography variant='paragraphMedium'>Manage</Typography>
            <TextField
              label='temporary dateOnAfter'
              value={dateOnAfterTmp}
              onChange={(event) => setDateOnAfterTmp(event.target.value)}
            />
            <Stack direction='row' gap={2}>
              <LoadingButton
                loading={isLoading}
                loadingIndicator={
                  <>
                    <CircularProgress size={20} />
                    &nbsp; Loading…
                  </>
                }
                variant='contained'
                onClick={() => {
                  dispatch(
                    preferencesV2Actions.updatePreferenceOverride(
                      preferencesV2Types.mkPreferenceV2({
                        id: 'dateOnAfter',
                        value: dateOnAfterTmp,
                      }),
                    ),
                  );
                  dispatch(transactionsActions.getTransactions());
                }}
              >
                Apply Permanently
              </LoadingButton>
              <LoadingButton
                loading={isLoading}
                loadingIndicator={
                  <>
                    <CircularProgress size={20} />
                    &nbsp; Loading…
                  </>
                }
                variant='outlined'
                onClick={() => {
                  dispatch(transactionsActions.getTransactions(undefined, { props: { dateOnAfter: dateOnAfterTmp } }));
                }}
              >
                Apply Temporary
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
};

export default TransactionsSettings;
